import React, { useState, useEffect } from 'react';

function getNiceMax(maxVal) {
  if (maxVal <= 4) return 4;
  if (maxVal <= 8) return 8;
  if (maxVal <= 10) return 10;
  if (maxVal <= 20) return 20;
  if (maxVal <= 40) return 40;
  if (maxVal <= 100) return 100;
  
  const order = Math.pow(10, Math.floor(Math.log10(maxVal)));
  const step = (order / 10) * 4;
  const rawMax = maxVal * 1.15;
  return Math.ceil(rawMax / step) * step;
}

export function LineChart({ points, period }) {
  const [hoveredPoint, setHoveredPoint] = useState(null);

  if (!points || points.length === 0) return null;

  const width = 800;
  const height = 220;
  const paddingLeft = 60;
  const paddingRight = 20;
  const paddingTop = 20;
  const paddingBottom = 40;

  const chartWidth = width - paddingLeft - paddingRight;
  const chartHeight = height - paddingTop - paddingBottom;

  const values = points.map((p) => p.value);
  const maxVal = Math.max(...values, 1);
  const yMax = getNiceMax(maxVal);
  const yMin = 0;
  const yRange = yMax;

  const getX = (index) => {
    if (points.length <= 1) return paddingLeft + chartWidth / 2;
    return paddingLeft + (index / (points.length - 1)) * chartWidth;
  };

  const getY = (val) => {
    return paddingTop + chartHeight - ((val - yMin) / yRange) * chartHeight;
  };

  const linePath = points
    .map((p, idx) => `${idx === 0 ? "M" : "L"} ${getX(idx)} ${getY(p.value)}`)
    .join(" ");

  const areaPath =
    points.length > 0
      ? `${linePath} L ${getX(points.length - 1)} ${paddingTop + chartHeight} L ${getX(0)} ${paddingTop + chartHeight} Z`
      : "";

  const gridLines = [0, 0.25, 0.5, 0.75, 1];

  const formatYLabel = (num) => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1).replace(/\.0$/, "")}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1).replace(/\.0$/, "")}K`;
    return Math.round(num).toString();
  };

  const xLabelIndices = [];
  if (points.length > 0) {
    const maxLabels = 5;
    const step = Math.max(1, Math.floor(points.length / (maxLabels - 1)));
    for (let i = 0; i < points.length; i += step) {
      xLabelIndices.push(i);
    }
    if (xLabelIndices[xLabelIndices.length - 1] !== points.length - 1) {
      xLabelIndices.push(points.length - 1);
    }
  }

  const formatXLabel = (ts) => {
    try {
      const date = new Date(ts);
      if (isNaN(date.getTime())) return ts;
      if (period === "24h") {
        return date.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
      }
      return date.toLocaleDateString(undefined, { month: "short", day: "numeric" });
    } catch {
      return ts;
    }
  };

  return (
    <div className="relative w-full">
      <svg
        viewBox={`0 0 ${width} ${height}`}
        className="w-full h-auto overflow-visible select-none"
      >
        <defs>
          <linearGradient id="chart-gradient" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#6366f1" stopOpacity="0.25" />
            <stop offset="100%" stopColor="#6366f1" stopOpacity="0.00" />
          </linearGradient>
          <filter id="glow" x="-20%" y="-20%" width="140%" height="140%">
            <feGaussianBlur stdDeviation="4" result="blur" />
            <feComposite in="SourceGraphic" in2="blur" operator="over" />
          </filter>
        </defs>

        {gridLines.map((ratio, idx) => {
          const val = yMax - ratio * yRange;
          const y = paddingTop + ratio * chartHeight;
          return (
            <g key={idx} className="opacity-40">
              <line
                x1={paddingLeft}
                y1={y}
                x2={width - paddingRight}
                y2={y}
                stroke="rgba(255, 255, 255, 0.05)"
                strokeWidth="1"
                strokeDasharray="4 4"
              />
              <text
                x={paddingLeft - 12}
                y={y + 4}
                textAnchor="end"
                className="fill-white/40 text-[10px] font-mono font-medium"
              >
                {formatYLabel(val)}
              </text>
            </g>
          );
        })}

        {xLabelIndices.map((idx) => {
          const x = getX(idx);
          const p = points[idx];
          return (
            <text
              key={idx}
              x={x}
              y={height - paddingBottom + 20}
              textAnchor="middle"
              className="fill-white/30 text-[10px] font-mono"
            >
              {formatXLabel(p.ts)}
            </text>
          );
        })}

        <path d={areaPath} fill="url(#chart-gradient)" />

        <path
          d={linePath}
          fill="none"
          stroke="#6366f1"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          filter="url(#glow)"
        />

        {hoveredPoint && (
          <line
            x1={hoveredPoint.x}
            y1={paddingTop}
            x2={hoveredPoint.x}
            y2={paddingTop + chartHeight}
            stroke="rgba(99, 102, 241, 0.3)"
            strokeWidth="1.5"
            strokeDasharray="3 3"
            className="pointer-events-none"
          />
        )}

        {points.map((p, idx) => {
          const x = getX(idx);
          const y = getY(p.value);
          const isHovered = hoveredPoint?.ts === p.ts;
          return (
            <g
              key={idx}
              onMouseEnter={() => setHoveredPoint({ x, y, ts: p.ts, value: p.value })}
              onMouseLeave={() => setHoveredPoint(null)}
              className="cursor-pointer"
            >
              <circle cx={x} cy={y} r="16" fill="transparent" />
              <circle
                cx={x}
                cy={y}
                r={isHovered ? "5" : "3"}
                className="transition-all duration-150"
                fill={isHovered ? "#FFFFFF" : "#6366f1"}
                stroke={isHovered ? "#6366f1" : "#070a13"}
                strokeWidth={isHovered ? "2.5" : "1.5"}
              />
            </g>
          );
        })}
      </svg>

      {hoveredPoint && (
        <div
          className="absolute z-10 bg-[#0c1020]/90 backdrop-blur-md border border-white/10 rounded-xl px-3 py-2 text-xs font-medium shadow-2xl pointer-events-none transition-all duration-75 flex flex-col gap-0.5"
          style={{
            left: `${(hoveredPoint.x / width) * 100}%`,
            top: `${(hoveredPoint.y / height) * 100}%`,
            transform: "translate(-50%, -120%)",
          }}
        >
          <span className="text-white/40 font-mono text-[9px] uppercase tracking-wider">
            {period === "24h"
              ? new Date(hoveredPoint.ts).toLocaleTimeString(undefined, {
                  month: "short",
                  day: "numeric",
                  hour: "numeric",
                  minute: "2-digit",
                })
              : new Date(hoveredPoint.ts).toLocaleDateString(undefined, {
                  month: "short",
                  day: "numeric",
                  year: "numeric",
                })}
          </span>
          <span className="text-white font-bold text-sm">
            {hoveredPoint.value.toLocaleString()}
          </span>
        </div>
      )}
    </div>
  );
}

export function AnalyticsChart({ userId, serverId }) {
  const [points, setPoints] = useState([]);
  const [period, setPeriod] = useState("30d");
  const [metric, setMetric] = useState("characters");
  const [loading, setLoading] = useState(true);

  const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:4000/api/admin';

  useEffect(() => {
    let active = true;
    setLoading(true);
    fetch(`${API_BASE}/users/${userId}/servers/${serverId}/analytics?period=${period}&metric=${metric}`, {
      credentials: "include",
    })
      .then((res) => {
        if (!res.ok) throw new Error("Failed to fetch analytics");
        return res.json();
      })
      .then((data) => {
        if (active) {
          setPoints(data.points || []);
        }
      })
      .catch((err) => {
        console.error(err);
      })
      .finally(() => {
        if (active) {
          setLoading(false);
        }
      });

    return () => {
      active = false;
    };
  }, [userId, serverId, period, metric, API_BASE]);

  return (
    <div className="glass-panel border border-white/5 rounded-2xl p-5">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
        <div>
          <h3 className="text-sm font-black text-white">Character Usage Analysis</h3>
          <p className="text-[10px] text-slate-500 mt-0.5">Translation volume over time</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex gap-1 bg-slate-950/50 p-1 rounded-lg border border-white/5">
            {["characters", "messages"].map((m) => (
              <button
                key={m}
                onClick={() => setMetric(m)}
                className={`text-[9px] font-mono px-2 py-1 rounded-md transition-colors ${
                  metric === m
                    ? "bg-indigo-500/20 text-indigo-300 font-bold"
                    : "text-slate-400 hover:text-slate-200"
                }`}
              >
                {m.toUpperCase()}
              </button>
            ))}
          </div>
          <div className="w-px h-4 bg-white/10" />
          <div className="flex gap-1 bg-slate-950/50 p-1 rounded-lg border border-white/5">
            {["24h", "7d", "30d"].map((p) => (
              <button
                key={p}
                onClick={() => setPeriod(p)}
                className={`text-[9px] font-mono px-2 py-1 rounded-md transition-colors ${
                  period === p
                    ? "bg-indigo-500/20 text-indigo-300 font-bold"
                    : "text-slate-400 hover:text-slate-200"
                }`}
              >
                {p.toUpperCase()}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="mt-4">
        {loading ? (
          <div className="w-full h-40 bg-white/5 rounded-xl animate-pulse" />
        ) : points.length === 0 ? (
          <p className="text-slate-500 text-xs text-center py-12">
            No data available for selected period.
          </p>
        ) : (
          <LineChart points={points} period={period} />
        )}
      </div>
    </div>
  );
}
